/** Append-only audit log. Retained indefinitely. */

export type AuditEntity =
  | 'form'
  | 'folder'
  | 'submission'
  | 'iq'
  | 'email'
  | 'teams'
  | 'user'
  | 'audit';

export type AuditAction =
  // forms
  | 'form.created'
  | 'form.published'
  | 'form.archived'
  | 'form.unarchived'
  | 'form.deleted'
  | 'form.moved'
  | 'folder.created'
  | 'folder.renamed'
  | 'folder.moved'
  | 'folder.deleted'
  | 'settings.updated'
  | 'field.added'
  | 'field.edited'
  | 'field.deleted'
  | 'field.reordered'
  | 'field.required_toggled'
  // submissions
  | 'submission.received'
  | 'field.amended'
  | 'approval.decided'
  | 'approval.reminded'
  // integrations
  | 'card.posted'
  | 'teams.installed'
  | 'teams.removed'
  | 'teams.notified'
  | 'iq.lead.created'
  | 'iq.lead.updated'
  | 'iq.customer.created'
  | 'iq.customer.updated'
  | 'iq.supplier.created'
  | 'iq.supplier.updated'
  | 'email.sent'
  | 'email_template.updated'
  | 'email_template.reset'
  | 'email_template.restored'
  // portal users
  | 'user.added'
  | 'user.updated'
  | 'user.removed'
  // audit log
  | 'audit.exported';

export interface AuditEntry {
  id: string;
  entityType: AuditEntity;
  entityId: string;
  action: AuditAction;
  actor: string;
  before: unknown | null;
  after: unknown | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Audit log viewer (admin) — entries as the API presents them.
// ---------------------------------------------------------------------------

export type AuditCategory = 'approvals' | 'applications' | 'iq' | 'emails' | 'teams' | 'forms' | 'users';

export const AUDIT_CATEGORIES: { key: AuditCategory; label: string }[] = [
  { key: 'approvals', label: 'Decisions' },
  { key: 'applications', label: 'Applications' },
  { key: 'iq', label: 'iQ' },
  { key: 'emails', label: 'Emails' },
  { key: 'teams', label: 'Teams' },
  { key: 'forms', label: 'Forms' },
  { key: 'users', label: 'Users and settings' },
];

/** What an entry is about, with an admin-app link when there is one. */
export interface AuditSubject {
  kind: 'application' | 'form' | 'folder' | 'user' | 'settings' | 'other';
  id: string;
  label: string;
  /** Admin-app path, e.g. `/suppliers?open=…`. */
  href: string | null;
}

/** One before → after change. Bank details and secrets are already masked. */
export interface AuditChange {
  field: string;
  before: string;
  after: string;
}

export interface AuditViewEntry {
  id: string;
  at: string;
  actor: string;
  category: AuditCategory;
  action: string;
  /** Plain-English sentence, e.g. "elvin@… approved Bulrush Ltd (Supplier)". */
  summary: string;
  subject: AuditSubject | null;
  /** Extra detail: a decline reason, an iQ message… */
  note: string | null;
  changes: AuditChange[];
}

export interface AuditListResponse {
  entries: AuditViewEntry[];
  /** Pass back as `cursor` for the next (older) page; null at the end. */
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Email templates (admin)
// ---------------------------------------------------------------------------

export interface EmailTemplateView {
  key: string;
  name: string;
  when: string;
  subject: string;
  /** Plain-text message (see Settings → Email templates for the format). */
  source: string;
  enabled: boolean;
  /** Saved in Settings rather than the built-in wording. */
  edited: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface EmailTemplateVersion {
  id: string;
  subject: string;
  source: string;
  enabled: boolean;
  note: 'saved' | 'reset' | 'restored';
  savedBy: string;
  savedAt: string;
}
